import React from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Stepper,
  Step,
  StepLabel,
  StepContent
} from '@mui/material';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';

interface EmailIdTokenScenarioProps {
  onBack: () => void;
}

const EmailIdTokenScenario: React.FC<EmailIdTokenScenarioProps> = ({ onBack }) => {
  return (
    <Box>
      <Typography variant="h5" gutterBottom color="primary">
        Add Email Claim to ID Token
      </Typography>
      
      <Typography variant="body1" paragraph>
        This scenario guides you through adding the email claim to ID tokens issued by Microsoft Entra ID.
        The email claim contains the user's primary email address and can be useful for applications that 
        need to identify users by their email address.
      </Typography>
      
      <Stepper orientation="vertical">
        <Step active>
          <StepLabel>Select and test your application</StepLabel>
          <StepContent>
            <Typography variant="body2" paragraph>
              Navigate to the Applications section using the sidebar, find your application, and click "Select" to open the App Tester in the right panel.
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Button variant="contained" size="small">Continue</Button>
            </Box>
          </StepContent>
        </Step>
        
        <Step active>
          <StepLabel>Navigate to Token Configuration</StepLabel>
          <StepContent>
            <Typography variant="body2" paragraph>
              In the App Tester sidebar menu, select "Token Configuration" to access the optional claims settings.
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Button variant="contained" size="small">Continue</Button>
            </Box>
          </StepContent>
        </Step>
        
        <Step active>
          <StepLabel>Add the email claim to ID Token</StepLabel>
          <StepContent>
            <Typography variant="body2" paragraph>
              In the Token Configuration section:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Click 'Add optional claim'" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Select 'ID' as the token type" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Check the 'email' claim" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Click 'Add Claim'" />
              </ListItem>
            </List>
            <Box sx={{ mb: 2 }}>
              <Button variant="contained" size="small">Continue</Button>
            </Box>
          </StepContent>
        </Step>
        
        <Step active>
          <StepLabel>Apply Changes</StepLabel>
          <StepContent>
            <Typography variant="body2" paragraph>
              Click the "Apply Changes" button to save the configuration to your application.
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Button variant="contained" size="small">Continue</Button>
            </Box>
          </StepContent>
        </Step>
        
        <Step active>
          <StepLabel>Test the Configuration</StepLabel>
          <StepContent>
            <Typography variant="body2" paragraph>
              Navigate to the "Fetch Access Token" section in the App Tester sidebar to test your configuration:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Click 'Fetch Access Token' in the sidebar" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Complete the authentication process (for selected test user)" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Click 'Copy Raw ID Token' button in the session details" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Navigate to Tools → JWT Token Decoder using the main sidebar" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Paste the ID token and click 'Decode Token'" />
              </ListItem>
              <ListItem>
                <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Verify that the email claim is present in the token payload" />
              </ListItem>
            </List>
            <Box sx={{ mb: 2 }}>
              <Button variant="contained" size="small">Finish</Button>
            </Box>
          </StepContent>
        </Step>
      </Stepper>
      
      <Box sx={{ mt: 4, p: 2, bgcolor: '#f0f7ff', borderRadius: 1 }}>
        <Typography variant="subtitle2" gutterBottom>
          Important Notes
        </Typography>
        <List dense>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="info" /></ListItemIcon>
            <ListItemText 
              primary="ID Token vs Access Token" 
              secondary="ID tokens contain user identity information, while access tokens (shown in the lower right panel) are for API authorization" 
            />
          </ListItem>
          <ListItem>
            <ListItemIcon sx={{ minWidth: 24 }}><ArrowRightIcon fontSize="small" color="info" /></ListItemIcon>
            <ListItemText 
              primary="JWT Token Decoder Tool" 
              secondary="Use the separate JWT Token Decoder tool in the Tools section to decode copied ID tokens" 
            />
          </ListItem>
        </List>
      </Box>
      
      <Box sx={{ mt: 4 }}>
        <Button 
          variant="outlined" 
          startIcon={<ArrowRightIcon />}
          onClick={onBack}
        >
          Back to All Scenarios
        </Button>
      </Box>
    </Box>
  );
};

export default EmailIdTokenScenario;